import { env } from 'config';

import Logger from 'utils/logger';

declare global {
  interface Window {
    _hsq: Array<[string, ...unknown[]]>;
  }
}

type HubSpotUserProperties = {
  email: string;
  [key: string]: unknown;
};

let isInitialized = false;
let initializationPromise: Promise<void> | null = null;
let currentUserEmail: string | null = null;

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById('hs-script-loader') as HTMLScriptElement;

    // If script exists and has loaded successfully, resolve immediately
    if (existingScript && existingScript.src && window._hsq) {
      // Verify HubSpot is actually loaded by checking if the queue is functional
      if (typeof window._hsq.push === 'function') {
        resolve();
        return;
      }
      // If script exists but HubSpot isn't loaded, remove it and retry
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = 'hs-script-loader';
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = `//js-eu1.hs-scripts.com/${env.REACT_APP_HUBSPOT_PORTAL_ID}.js`;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error('Failed to load HubSpot script'));
    };
    document.head.appendChild(script);
  });
}

function getQueue(): Array<[string, ...unknown[]]> {
  window._hsq = window._hsq || [];
  return window._hsq;
}

export function initHubSpot(user: HubSpotUserProperties): Promise<void> {
  // If already initialized for the same user, skip
  if (isInitialized && currentUserEmail === user.email) {
    return Promise.resolve();
  }

  if (!env.REACT_APP_HUBSPOT_PORTAL_ID) {
    Logger.warn('HubSpot portal ID not configured, skipping initialization');
    return Promise.resolve();
  }

  // If initialization is already in progress, wait for it to complete
  if (initializationPromise) {
    return initializationPromise.then(() => {
      // After the previous initialization completes, check if we need to reset for a different user
      if (isInitialized && currentUserEmail !== user.email) {
        resetHubSpot(true); // Clear user data when switching to a different user
        // Recursively call initHubSpot to initialize for the new user
        return initHubSpot(user);
      }
      return Promise.resolve();
    });
  }

  // If already initialized for a different user, reset first to clear conversation data
  if (isInitialized && currentUserEmail !== user.email) {
    resetHubSpot(true); // Clear user data when switching to a different user
  }

  // Initialize the queue early so events can be queued before script loads
  getQueue();

  initializationPromise = (async () => {
    try {
      await loadScript();

      const hsq = getQueue();
      const identifyProperties: Record<string, unknown> = {
        email: user.email,
      };

      hsq.push(['identify', identifyProperties]);
      hsq.push(['trackPageView']);

      isInitialized = true;
      currentUserEmail = user.email;
      initializationPromise = null;
    } catch (error) {
      Logger.warn('Failed to initialize HubSpot', error);
      // Reset promise so retry is possible
      initializationPromise = null;
      isInitialized = false;
      currentUserEmail = null;
      throw error;
    }
  })();

  return initializationPromise;
}

export function resetHubSpot(clearUserData = false): void {
  isInitialized = false;
  initializationPromise = null;
  currentUserEmail = null;

  // Only revoke cookie consent when switching to a different user
  // This prevents conversation history from being cleared when the same user logs back in
  if (clearUserData) {
    // Use HubSpot's official method to revoke user identity and clear cookies
    const hsq = getQueue();
    hsq.push(['revokeCookieConsent']);
  }
}

export function trackHubSpotEvent(eventName: string, properties?: Record<string, unknown>): void {
  // Initialize queue if not already done - HubSpot's queue system can handle events
  // queued before the script loads, so we queue events even if not initialized yet
  const hsq = getQueue();
  hsq.push(['trackCustomBehavioralEvent', { name: eventName, properties }]);
}
